import React, { memo, useCallback, useMemo } from 'react';
import clsx from 'clsx';

import { specifyAnotherField } from '_constants/onboardingConstants';
import { TranslateValueType } from '_types';
import { ProfileFieldErrorType } from '_types/profile.interface';

import SelectButton from 'app/components/SelectButton/SelectButton';
import ErrorCloud from 'app/components/ErrorCloud';

import {
  checkOtherSelectButton,
  getSpecifyOtherValueFromList,
  removeSpecifyOtherValueFromList,
} from 'utils/onboarding';

import {
  NONE_FUNCTIONAL,
  SELECT_ALL_FUNCTIONAL,
  getAllSelectableValues,
  getFunctionalValue,
  isSelectAllChecked,
  getValuesForSubmission,
  getFormListHeight,
  isItemSelected,
  getSpecifyAnotherFieldValue,
} from './utils/helpers';

import { ListWithSelectablesProps } from './types/types';
import './style.scss';

const ListWithSelectables: React.FC<ListWithSelectablesProps> = ({
  type,
  value,
  availableValuesList,
  onChange,
  errors,
  look = 'two-columns',
  disableHeightLimitation = false,
  fillRowsFirst = false,
}) => {
  // Memoized specifyValue for "Specify another" field
  const specifyValue = useMemo(() => {
    if (type === 'checkbox') {
      return getSpecifyOtherValueFromList(value, availableValuesList);
    }
    return value;
  }, [value, availableValuesList, type]);

  // Memoized list height
  const formListHeight = useMemo(
    () => getFormListHeight(availableValuesList),
    [availableValuesList],
  );

  // Memoized functional values
  const selectAllValue = getFunctionalValue(
    availableValuesList,
    SELECT_ALL_FUNCTIONAL,
  );
  const noneValue = getFunctionalValue(availableValuesList, NONE_FUNCTIONAL);

  // Checkbox add/remove logic
  const handleCheckboxChange = useCallback(
    (name: string) => {
      if (type !== 'checkbox') return;

      let newList = [...value];

      if (selectAllValue && name === selectAllValue) {
        if (isSelectAllChecked(value, availableValuesList)) {
          // Unchecking "Select All" - clear everything, including "Other"
          onChange([]);
        } else {
          // Checking "Select All" - select all standard options, keep "Other" if it was already selected
          const all = getAllSelectableValues(availableValuesList);
          const otherSelected = value.includes(specifyAnotherField);
          onChange(otherSelected ? [...all, specifyAnotherField] : all);
        }
        return;
      }

      if (noneValue && name === noneValue) {
        if (value.length === 1 && value[0] === noneValue) {
          onChange([]);
        } else {
          onChange([noneValue]);
        }
        return;
      }

      if (noneValue && newList.includes(noneValue)) {
        newList = [];
      }

      if (newList.includes(name)) {
        newList = newList.filter((v) => v !== name);
      } else {
        newList = [...newList, name];
      }

      if (selectAllValue) {
        newList = newList.filter((v) => v !== selectAllValue);
      }

      if (newList.length === 0) {
        onChange([]);
        return;
      }

      onChange(newList);
    },
    [type, value, availableValuesList, onChange, selectAllValue, noneValue],
  );

  const handleSpecifyValueChange = useCallback(
    (newValue: string) => {
      if (type !== 'checkbox') return;

      const newList = removeSpecifyOtherValueFromList(
        value,
        availableValuesList,
      );
      if (newValue) {
        onChange([...newList, newValue]);
      } else {
        onChange(newList);
      }
    },
    [type, value, availableValuesList, onChange],
  );

  const renderSelectButton = (elem: TranslateValueType, index: number) => {
    const isCheckbox = type === 'checkbox';

    if (isCheckbox && elem.value === specifyAnotherField) {
      return (
        <SelectButton
          key={index}
          value={specifyValue}
          onClick={() => {
            if (checkOtherSelectButton(value, specifyValue, specifyValue)) {
              onChange(
                removeSpecifyOtherValueFromList(value, availableValuesList),
              );
            } else if (specifyValue) {
              onChange([
                ...removeSpecifyOtherValueFromList(value, availableValuesList),
                specifyValue,
              ]);
            }
          }}
          onChange={handleSpecifyValueChange}
          selected={checkOtherSelectButton(value, specifyValue, specifyValue)}
          type={type}
          inputSelect
          inputSelectText={elem.name}
          hint={elem.hint}
        />
      );
    }

    const selected = isCheckbox
      ? isItemSelected(elem, value, availableValuesList, noneValue)
      : elem.value === specifyAnotherField
        ? !!value && !availableValuesList.some((opt) => opt.value === value)
        : isItemSelected(
            elem,
            value ? [value] : [],
            availableValuesList,
            noneValue,
          );

    const specifyFieldValue = getSpecifyAnotherFieldValue(
      elem,
      value,
      availableValuesList,
      specifyValue,
    );

    if (isCheckbox) {
      return (
        <SelectButton
          key={index}
          value={specifyFieldValue}
          onClick={() => {
            handleCheckboxChange(elem.value);
          }}
          onChange={handleSpecifyValueChange}
          selected={selected}
          type={type}
          inputSelect={false}
          inputSelectText={elem.name}
          hint={elem.hint}
        />
      );
    }

    return (
      <SelectButton
        key={index}
        value={specifyFieldValue}
        onClick={(event) => {
          onChange(elem.value === specifyAnotherField ? event : elem.value);
        }}
        onChange={onChange}
        selected={selected}
        type="select"
        inputSelect={elem.value === specifyAnotherField}
        inputSelectText={specifyAnotherField}
      />
    );
  };

  return (
    <div className="list_with_selectables">
      {!!errors?.length && <ErrorCloud errorsListOrErrorText={errors} />}
      <div
        className={clsx('list_with_selectables__list', look, { fillRowsFirst })}
        style={{
          height:
            disableHeightLimitation || look === 'one-column'
              ? 'auto'
              : formListHeight,
        }}
      >
        {availableValuesList.map(renderSelectButton)}
      </div>
    </div>
  );
};

export default memo(ListWithSelectables);
export { getValuesForSubmission };
